import { CheckCircle2, ChevronDown, ChevronRight, Clock } from 'lucide-react'
import type {
  ConfirmationChannel, DurationUnit, EmployeeDto, ScheduleEventDto, ScheduleEventKind, SlotStartBody,
} from '../../../../lib/api/v2/planning'
import { formatDate } from '../../../../lib/format'
import { formatEventWhen } from '../planning-helpers'
import { cn } from '../../../../lib/utils'
import { ConfirmSlotForm, ReasonForm, ScheduleForm, AssigneeEditor, NotesForm, channelLabel } from './ProjectDetailForms'

/** Body for a reschedule submit (mirrors planningApi.rescheduleEvent). */
export type RescheduleBody = {
  durationUnit: DurationUnit
  durationValue: number
  assignedUserIds: string[]
  reason?: string
} & SlotStartBody

/** Which inline form is open ON A CRÉNEAU (one at a time, across the whole section). */
export type SlotForm = null | 'reschedule' | 'confirm' | 'cancel' | 'assign' | 'note'

interface WorkItemSlotCardProps {
  slot: ScheduleEventDto
  /**
   * Which work item this créneau belongs to — SOLY-PLAN-006 MS-04. Drives the testid prefix, the
   * vocabulary of the crew line and which form flavours are opened. Deliberately the SAME component
   * for both: rendering a métré's passages as field rows would invent a THIRD grammar next to the
   * pose's, which the UX session would then have to undo.
   */
  area: 'metre' | 'pose'
  kind: ScheduleEventKind
  /** 0-based position among the work item's live créneaux, and how many there are. */
  index: number
  total: number
  projectId: string
  /** The owning work item. Exactly one of the two is set, matching `area`. */
  installationId?: string
  technicalSurveyId?: string
  /**
   * Estimated crew size — drives the « n/m estimés » hint. `undefined` when the pose carries no
   * estimate (SOLY-PLAN-004): there is then no contract to be short of, so the card shows the crew
   * plainly instead of an amber count against a number nobody chose. A métré never passes it: it
   * has no crew contract, one surveyor is the whole question.
   */
  expectedResources?: number
  employees: EmployeeDto[]
  userNameById: Record<string, string>
  /** SOLY-PERM-001 — sans `planning:manage` la carte se lit : « voir dans l'agenda » survit, le reste part. */
  readOnly?: boolean
  expanded: boolean
  onToggle: () => void
  form: SlotForm
  onOpenForm: (f: SlotForm) => void
  onReschedule: (eventId: string, body: RescheduleBody) => Promise<void>
  onCancelEvent: (eventId: string, reason: string) => Promise<void>
  onConfirm: (eventId: string, channel: ConfirmationChannel, note: string | undefined) => Promise<void>
  onUnconfirm: (eventId: string) => Promise<void>
  onAssign: (eventId: string, userId: string) => void
  onUnassign: (eventId: string, userId: string) => void
  /** Writes this créneau's note — « ce qu'on fait dans CE bloc » (Élodie 2026-08-18). */
  onSetNote: (eventId: string, note: string | null) => Promise<void>
  /** Jump the agenda to this créneau (MS-12). Omitted → the gesture is not offered. */
  onFocusSlot?: (slot: ScheduleEventDto) => void
  /**
   * '' for phase 1, '-2', '-3'… beyond — the drawer's rule for every testid it emits. Without it a
   * chantier whose initial pose AND finition both carry créneaux emits `planning-pose-slot-0`
   * twice, and a strict Playwright selector matches two nodes.
   */
  phaseSuffix: string
}

/**
 * One créneau of a WORK ITEM, with its own gestures — SOLY-PLAN-003 MS-11, generalized to the
 * métré by SOLY-PLAN-006 MS-04.
 *
 * MS-09 listed the créneaux read-only and said so in the code: composing them was the agenda
 * fiche's job, and offering the same gesture on two surfaces was how they start to disagree.
 * Field feedback (Thierry, 2026-08-17) says the cockpit is the surface actually used — and the
 * read-only list was not neutral, it was a REGRESSION: `Placeurs`, `Date` and `Confirmation` lived
 * in the « exactly one créneau » branch, so a pose in two blocks silently lost all three.
 *
 * What keeps the two surfaces honest is not refusing the gesture, it is sharing the code: every
 * form below comes from ProjectDetailForms — the same ones the single-créneau rows used, and the
 * same ones the métré still uses. No twin implementation, here or there.
 */
export function WorkItemSlotCard({
  slot, area, kind, index, total, projectId, installationId, technicalSurveyId,
  expectedResources, employees, userNameById,
  readOnly = false, expanded, onToggle, form, onOpenForm, phaseSuffix,
  onReschedule, onCancelEvent, onConfirm, onUnconfirm, onAssign, onUnassign, onSetNote, onFocusSlot,
}: WorkItemSlotCardProps) {
  const confirmed = !!slot.confirmedAt
  // Every créneau of an open pose is confirmable, whatever its date. The card used to go read-only
  // once the créneau was over, on the reasoning that agreeing to a date gone by records nothing —
  // but that is what left the secretary with a block she could neither confirm nor stop being asked
  // about. A pose that was never confirmed and did not happen still has to be confirmed late or
  // moved. The list only renders while the installation is open, so a CLOSED pose (report recorded)
  // never reaches here — the confirmation question ends there, and only there.
  const crew = slot.assignments.map((a) => userNameById[a.assignedUserId] ?? 'Employé').join(', ')
  const incompleteCrew = expectedResources !== undefined
    && slot.assignments.length > 0 && slot.assignments.length < expectedResources
  const channel = slot.confirmationChannel ? channelLabel(slot.confirmationChannel) : null
  // The pose keeps its historical testids to the character (TC-019 & friends read them); the métré
  // gets `planning-metre-slot-N`, in the same convention. A métré has no phase suffix here: a
  // re-métré of phase ≥ 2 is its own section, rendered by its own MetreSection.
  const testId = `planning-${area}${phaseSuffix}-slot-${index}`
  const nobodyLabel = area === 'metre' ? 'aucun métreur' : 'aucun placeur'
  const assignLabel = area === 'metre' ? 'métreur' : 'placeurs'
  const close = () => onOpenForm(null)

  return (
    <li className="rounded-md border bg-background" data-testid={testId} data-confirmed={confirmed}>
      {/* The whole header is the toggle: a tap target the size of the card, because this list is
          read and driven on a tablet. */}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        data-testid={`${testId}-toggle`}
        className="flex w-full items-start gap-2 px-2 py-1.5 text-left text-xs hover:bg-muted/40"
      >
        {expanded
          ? <ChevronDown className="mt-0.5 h-3 w-3 flex-none text-muted-foreground" aria-hidden />
          : <ChevronRight className="mt-0.5 h-3 w-3 flex-none text-muted-foreground" aria-hidden />}
        <span className="min-w-0 flex-1">
          <span className="block">
            {/* Ordinality only from two créneaux on: « 1/1 » on the nominal pose would be noise
                about a composition that is not one (principe directeur, _design.md). */}
            {total > 1 && (
              /* Named, because « no ordinality » is asserted from the outside: scanning the card's
                 text for « 1/1 » also matches the date it renders right next to it (01/10/2026). */
              <span className="text-muted-foreground" data-testid={`${testId}-ordinality`}>{index + 1}/{total} · </span>
            )}
            <span className="font-medium text-foreground">{formatEventWhen(slot)}</span>
          </span>
          {/* « Défini » = équipe COMPLÈTE (≥ estimation) — la règle du ready-check et du Suivi
              « Équipe incomplète ». La ligne Placeurs que cette carte remplace affichait ce compte
              n/m en ambre ; sans lui, une pose à un seul placeur sur deux estimés se lit comme
              normale. */}
          {crew
            ? (
              <span className={cn('block truncate', incompleteCrew ? 'text-amber-600' : 'text-muted-foreground')}>
                {crew}
                {incompleteCrew && <> · {slot.assignments.length}/{expectedResources}</>}
              </span>
            )
            : <span className="block text-amber-600">{nobodyLabel}</span>}
        </span>
        <span className="flex-none" data-testid={`${testId}-state`}>
          {confirmed ? (
            <span className="inline-flex items-center gap-1 text-emerald-700">
              <CheckCircle2 className="h-3 w-3 flex-none" aria-hidden />
              <span className="font-medium">confirmé le {formatDate(slot.confirmedAt!)}</span>
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 font-medium text-amber-600">
              <Clock className="h-3 w-3 flex-none" aria-hidden /> Proposé
            </span>
          )}
        </span>
      </button>

      {expanded && (
        <div className="border-t px-2 py-1.5">
          {/* The confirmation recap stays readable while the card is open — channel and note are
              why a « confirmé » is trusted, and they were on the row this card replaces. */}
          {slot.label && (
            <p className="mb-1 whitespace-pre-line rounded bg-muted/40 px-1.5 py-1 text-[11px] text-foreground"
              data-testid={`${testId}-note-text`}>
              {slot.label}
            </p>
          )}
          {confirmed && (channel || slot.confirmationNote) && (
            <p className="mb-1 text-[11px] text-muted-foreground">
              {channel}
              {channel && slot.confirmationNote && ' · '}
              {slot.confirmationNote && `« ${slot.confirmationNote} »`}
            </p>
          )}

          {(!readOnly || onFocusSlot) && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {!readOnly && (
              <>
                <SlotAction testId={`${testId}-reschedule`} onClick={() => onOpenForm('reschedule')}>replanifier</SlotAction>
                <SlotAction testId={`${testId}-assign`} onClick={() => onOpenForm('assign')}>{assignLabel}</SlotAction>
                <SlotAction testId={`${testId}-note`} onClick={() => onOpenForm('note')}>
                  {slot.label ? 'modifier la note' : 'ajouter une note'}
                </SlotAction>
              </>
            )}
            {!readOnly && (
              <SlotAction testId={`${testId}-confirm`} onClick={() => onOpenForm('confirm')}>
                {confirmed ? 'reconfirmer' : 'confirmer'}
              </SlotAction>
            )}
            {!readOnly && confirmed && (
              <SlotAction testId={`${testId}-unconfirm`} onClick={() => void onUnconfirm(slot.id)}>
                remettre en proposé
              </SlotAction>
            )}
            {onFocusSlot && (
              <SlotAction testId={`${testId}-agenda`} onClick={() => onFocusSlot(slot)}>voir dans l’agenda</SlotAction>
            )}
            {!readOnly && (
              <button
                type="button"
                data-testid={`${testId}-cancel`}
                onClick={() => onOpenForm('cancel')}
                className="ml-auto text-[11px] text-muted-foreground hover:text-red-600 hover:underline"
              >
                annuler ce créneau
              </button>
            )}
          </div>
          )}

          {form === 'reschedule' && (
            <ScheduleForm
              area={area} kind={kind} projectId={projectId}
              installationId={installationId} technicalSurveyId={technicalSurveyId}
              employees={employees} initialUnit={slot.durationUnit} initialValue={slot.durationValue}
              expectedResources={expectedResources}
              onSubmit={() => Promise.resolve()}
              onCancel={close}
              reschedule={{ event: slot, onSubmit: (body) => onReschedule(slot.id, body) }}
            />
          )}
          {form === 'note' && (
            <NotesForm
              area={`${area}-slot`} initialNotes={slot.label ?? ''}
              onSubmit={(n) => onSetNote(slot.id, n || null)} onCancel={close}
              title="Note du créneau" maxLength={200}
              placeholder={area === 'metre' ? 'Ex : on remesure la salle de bain' : 'Ex : on pose les plans de travail'} />
          )}
          {form === 'confirm' && (
            <ConfirmSlotForm area={area} onSubmit={(c, n) => onConfirm(slot.id, c, n)} onCancel={close} />
          )}
          {form === 'cancel' && (
            <ReasonForm
              action="cancel-event" area={area}
              onSubmit={(r) => onCancelEvent(slot.id, r)} onCancel={close}
              /* The dialog NAMES what moves, it does not ask « êtes-vous sûr » — convention from the
                 kind reclassement of SOLY-PLAN-002. Both consequences are real and invisible
                 otherwise: the downstream anchor is the LATEST créneau (WorkItemSlots.Selected), and
                 a pose with no live créneau left goes back to « à planifier ». */
              notice={cancelNotice(area, index, total)}
            />
          )}
          {form === 'assign' && (
            /* The SAME editor the métré section and the pose section open — moved into
               ProjectDetailForms for that. A copy here is how one gesture starts behaving
               differently depending on where it was opened from. */
            <AssigneeEditor
              event={slot} kind={kind} area={area}
              employees={employees} userNameById={userNameById}
              onAssign={(uid) => onAssign(slot.id, uid)} onUnassign={(uid) => onUnassign(slot.id, uid)}
              onClose={close}
            />
          )}
        </div>
      )}
    </li>
  )
}

/**
 * What cancelling THIS créneau actually costs — indexed, because it is not the same sentence, and
 * not the same one for a métré as for a pose. The dialog NAMES what moves, it never asks
 * « êtes-vous sûr » (convention from the kind reclassement of SOLY-PLAN-002).
 */
function cancelNotice(area: 'metre' | 'pose', index: number, total: number): string {
  if (area === 'metre') {
    if (total === 1) {
      return 'C’est le seul créneau de ce métré : il repassera « à planifier ».'
    }
    if (index === total - 1) {
      // M2/M3 anchor on the LAST créneau (SelfAnchor.LastSlot) and M6 covers the passages behind
      // the report — cancelling the last one moves both back onto the previous passage.
      return 'C’est le dernier passage du métré : les alertes de délai avant la pose et le rapport se recaleront sur le passage précédent.'
    }
    return 'Les autres passages du métré ne bougent pas.'
  }
  if (total === 1) {
    return 'C’est le seul créneau de cette pose : elle repassera « à planifier », et le chantier reviendra dans l’Inbox.'
  }
  if (index === total - 1) {
    return 'C’est le dernier créneau de la pose : l’échéance de facturation et les alertes de clôture, qui s’y accrochent, reculeront sur le créneau précédent.'
  }
  return 'Les autres créneaux de la pose ne bougent pas ; le temps annulé redevient « à planifier ».'
}

/** Discreet inline action, the cockpit's language for a gesture that is not a step of the flow. */
function SlotAction({ testId, onClick, children }: { testId: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" data-testid={testId} onClick={onClick}
      className="text-[11px] font-medium text-primary hover:underline">
      {children}
    </button>
  )
}
